import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const AutomationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.background};
  min-height: 100vh;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ChannelCard = styled(motion.div)<{ $disabled?: boolean }>`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleX(0);
    transition: transform ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  }
  
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-4px);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const ChannelIconWrapper = styled.div<{ $color?: string }>`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color, theme }) => $color || theme.colors.primary}15;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  svg {
    color: ${({ $color, theme }) => $color || theme.colors.primary};
  }
`;

const ChannelName = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const ChannelDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.warning};
  color: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const channels = [
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Automação de envio de emails, respostas inteligentes e sequências de follow-up',
    icon: Mail,
    color: '#ff3b30',
    available: true,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'Automação de mensagens WhatsApp Business com templates e gatilhos personalizados',
    icon: MessageSquare,
    color: '#00ff88',
    available: false,
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Automação de notificações e mensagens em canais e DMs do Slack',
    icon: Send,
    color: '#00aaff',
    available: false,
  },
];

const Automations: React.FC = () => {
  const navigate = useNavigate();
  
  const handleChannelClick = (channelId: string, available: boolean) => {
    if (!available) return;
    
    if (channelId === 'gmail') {
      navigate('/automations/gmail');
    } else if (channelId === 'whatsapp') {
      navigate('/automations/whatsapp');
    } else if (channelId === 'slack') {
      navigate('/automations/slack');
    }
  };
  
  return (
    <AutomationsContainer>
      <Header>
        <Title>Automações</Title>
        <Subtitle>
          Crie fluxos automáticos para seus canais de comunicação
        </Subtitle>
      </Header>
      
      <ChannelsGrid>
        {channels.map((channel, index) => (
          <ChannelCard
            key={channel.id}
            $disabled={!channel.available}
            onClick={() => handleChannelClick(channel.id, channel.available)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: index * 0.1,
              duration: 0.3,
              ease: 'easeOut'
            }}
            whileHover={channel.available ? { scale: 1.02 } : {}}
            whileTap={channel.available ? { scale: 0.98 } : {}}
          >
            <ChannelIconWrapper $color={channel.color}>
              <channel.icon size={32} />
            </ChannelIconWrapper>
            
            <ChannelName>
              {channel.name}
              {!channel.available && <ComingSoonBadge>Em breve</ComingSoonBadge>}
            </ChannelName>
            
            <ChannelDescription>
              {channel.description}
            </ChannelDescription>
          </ChannelCard>
        ))}
      </ChannelsGrid>
    </AutomationsContainer>
  );
};

export default Automations;
